import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, ObservableInput, of, shareReplay, switchMap, tap } from 'rxjs';
import { LayoutService } from '../layout/layout.service';
import { ApCognitoUser } from '../../models/user';
import { StaticDataCollection, ViewValue } from '../../models/global';
import { ContentItemCollection } from '../content/content.service';
import { ResidentPhoto, ResidentPreferenceData } from '@resparke/models/resident.model';
import { Facility, Resident, ResidentFamily } from '@resparke/models/client-types';

export type PreferenceCategories = Record<string, ViewValue[]>;
/**
 * Register you State properties here
 */
interface AppState {
  currentRoute: BehaviorSubject<string[]>;
  currentResidentId: BehaviorSubject<string>;
  currentResident: BehaviorSubject<Resident>;
  residentFamily: BehaviorSubject<ResidentFamily>;
  currentResidentPreferenceData: BehaviorSubject<ResidentPreferenceData>;
  currentFacilityId: BehaviorSubject<string>;
  currentFacility: BehaviorSubject<Facility>;
  organisationId: BehaviorSubject<string>;
  currentUser: BehaviorSubject<ApCognitoUser>;
  contentItemCollection: BehaviorSubject<ContentItemCollection>;
  preferenceCategories: BehaviorSubject<PreferenceCategories>;
  staticData: BehaviorSubject<StaticDataCollection>;
  residentPhotos: BehaviorSubject<ResidentPhoto[]>;
  objectUrlCache: BehaviorSubject<Set<string>>;
}

type AppStateValues = {
  [K in keyof AppState]: AppState[K] extends BehaviorSubject<infer U> ? U : never;
};


@Injectable({
  providedIn: 'root'
})
export class  AppStateService {
  private layoutService = inject(LayoutService);
  isMobile$ = this.layoutService.isMobile$;
  responsive$ = this.layoutService.isResponsive$;

   private preloadStateFns: { [key in keyof AppState]?: Function } = {}

  preload(key: keyof AppState, fn: Function) {
    this.preloadStateFns[key] = fn;
  }

  /**
   * State is our main Observable that will never be completed.
   * It consist of a collection of child values that are of type BehaviorSubject.
   * If a property doesn't exists yet, the setState will create the required BehaviorSubject.
   * When the state is cleared (on logout), the child properties Observables are completed and removed.
   *
   * By using the `get$` function to subscribe to a child Observable, we will never run into
   * a reference collision, because the State will never be recreated during the app.
   * Or use `get` to get the direct value of the BehaviorSubject
  */
  private _state = new Map<keyof AppState, BehaviorSubject<any>>();

  /**
   *
   * @param key
   * @param val If you want to empty a State property but don't want to update the Observable, set a value of `null`.
   *  Do not use undefined, because then the Observable will be recreated.
   */
  set<K extends keyof AppStateValues>(key: K, val: AppStateValues[K]) {
    // Create the BehaviorSubject if it doesn't exist
    if (!this._state.has(key)) {
      this._state.set(key, new BehaviorSubject(val));
    } else {
      this._state.get(key).next(val);
    }
  }

  /**
   * Returns the current Observable of the state property.
   */
  get$<K extends keyof AppStateValues>(key: K): Observable<AppStateValues[K]> {
    // Create the BehaviorSubject if it doesn't exist
    if (!this._state.has(key)) {
      this._state.set(key, new BehaviorSubject(undefined));
    }
    return this._state.get(key) as Observable<AppStateValues[K]>;
  }

  /**
   * Returns the current value of the state property
   */
  get<K extends keyof AppStateValues>(key: K): AppStateValues[K] | undefined {
    return this._state.has(key) ? this._state.get(key)!.value : undefined;
  }

  getFromCollection<T extends { id: string }>(id: string, collectionKey: keyof AppState): T | undefined {
    const collection = this.get(collectionKey) as unknown as T[];
    return Array.isArray(collection) ? collection.find(val => val.id === id) : undefined;
  }

  /**
   * @description Get data from a collection (map) if available or otherwise fetch the data from the preload function in the preloadService
   * @param id
   * @param collectionKey
   * @returns Array of T
   */
  private preloadInProgress = new Map<string, Observable<any>>();
  private createPreloadKey(collectionKey: keyof AppState, id: string): string {
    return `${String(collectionKey)}_${id}`;
  }
  getFromCollection$<T>(id: string, collectionKey: keyof AppState): Observable<T> {

    const collection = this.get$(collectionKey);

    return collection.pipe(
      switchMap(col => {
        // If data exists, return it
        if (col && typeof col === 'object' && col !== null && !Array.isArray(col) && id in col) {
          return of((col as Record<string, any>)[id]) as Observable<T>;
        }

        // Check if preload function exists
        if (!this.preloadStateFns[collectionKey]) {
          console.warn(`No preload function registered for ${collectionKey} and also no existing data resolved`);
          return of(undefined) as Observable<T>;
        }

        // Create unique key for this preload operation
        const preloadKey = this.createPreloadKey(collectionKey, id);

        // If preload is already in progress, return the existing observable
        if (this.preloadInProgress.has(preloadKey)) {
          return this.preloadInProgress.get(preloadKey) as Observable<T>;
        }

        // Start new preload operation
        const preloadObservable = this.preloadStateFns[collectionKey](id).pipe(
          tap(() => {
            // Clean up the preload tracking when complete
            this.preloadInProgress.delete(preloadKey);
          }),
          // Handle errors and cleanup
          catchError(error => {
            this.preloadInProgress.delete(preloadKey);
            throw error;
          }),
          // Share and replay the observable to prevent multiple executions
          shareReplay(1)
        ) as Observable<T>;

        // Track this preload operation
        this.preloadInProgress.set(preloadKey, preloadObservable);
        return preloadObservable as Observable<T>;
      })
    );
  }
/**
   * @description Clear the appstate from all cached data, except `viewMode` which may be used in subscription management.
   * @param param0
   */
  clearState({ prefill }: { prefill?: Partial<Record<keyof AppState, any>> } = {}) {
    this.preloadInProgress.clear();

    // CRITICAL: Clean up object URLs before clearing state
    const objectUrls = this.get('objectUrlCache');
    if (objectUrls) {
      objectUrls.forEach(url => URL.revokeObjectURL(url));
    }

    // Initialize objectUrlCache first (as in constructor)
    this.set('objectUrlCache', new Set());

    // Add prefilled values
    if (prefill) {
      for (const key of Object.keys(prefill) as (keyof AppState)[]) {
        this.set(key, prefill[key]);
      }
    }
  }

  constructor() {
    this.set('objectUrlCache', new Set());
  }

  /**
   * Content item collection
   */

  private _contentItemCollection = new BehaviorSubject<ContentItemCollection>({});
  contentItemCollection$ = this._contentItemCollection.asObservable();

  set contentItemCollection(collection: ContentItemCollection) {
    this._contentItemCollection.next(collection)
  }

  get contentItemCollection() {
    return this._contentItemCollection.value;
  }

  /**
   * Reinitialise the (Behaviour)Subjects because they where completed and no longer emitting values
   */
  initState() {
    this.clearState();
  }
}
